import React, { FC, useContext, useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';
import { AuthContext } from '../../../../context/auth';
import { useCheckPinMutation } from '../OrderModal/mutation';
import { useUserQuery } from '../../../../hooks/query';
import { takeContractId } from '../../../../utils/refetch';
import BaseModal from '../BaseModal/BaseModal';

type Props = {
  centered: boolean;
  show: boolean;
  selectedButton: string;
  onHide: () => void;
  handleSuccess: () => void;
  streamLimitReached?: boolean;
};

const PinConfirmationModal: FC<Props> = ({
                                           show,
                                           onHide,
                                           handleSuccess,
                                           selectedButton,
                                         }) => {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [validationTriggered, setValidationTriggered] = useState<boolean>(false);
  const authContext = useContext(AuthContext);
  const { userLocation } = authContext;
  useUserQuery(true, takeContractId() != null);

  const { mutate, isLoading } = useCheckPinMutation();

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 4) {
      setPin(value);
    }
  };

  const handleBlur = () => {
    setValidationTriggered(true);
  };

  const handleCheckPin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setValidationTriggered(true);
    if (pin.length < 4) {
      return;
    }

    try {
      mutate(
        { pin: pin },
        {
          onSuccess(data: any) {
            if (data.status === 'success') {
              handleSuccess();
            }
          },
          onError(error: any) {
            setError(error?.body.message);
            setPin('');
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleHide = () => {
    setPin('');
    setError('');
    setValidationTriggered(false);
    onHide();
  };

  return (
    <BaseModal
      open={show}
      onClose={handleHide}
      title={`${userLocation === 'DE' ? '18+' : 'Sky'} PIN`}
      actions={
        <Box display="flex" justifyContent="center" width="100%">
          <Button
            variant="contained"
            onClick={handleCheckPin}
            disabled={pin?.length < 4 || isLoading}
            size="large"
            className="primary"
            sx={{ color: 'white' }}
          >
{isLoading ? 'ÜBERPRÜFUNG...' : 'ABSENDEN'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ mt: 2 }}>
        {/* Content Warning */}
        {(selectedButton === 'hd' || selectedButton === 'sd') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
              Der Inhalt ist nach dem Download nicht zugriffsbeschränkt. Er darf Minderjährigen unter keinen
              Umständen zugänglich gemacht werden.
            </Typography>
          </Alert>
        )}

        {/* PIN Description */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          Um diesen Film ansehen zu können, müssen Sie hier Ihre {userLocation === 'DE' ? '18+' : 'Sky'} PIN
          eingeben.
        </Typography>

        {/* PIN Input */}
        <TextField
          name="pin"
          label={userLocation === 'DE' ? '18+ PIN' : 'Sky PIN'}
          slotProps={{
            input: {
              inputProps: {
                maxLength: 4
              }
            }
          }}
          value={pin}
          onChange={handlePinChange}
          onBlur={handleBlur}
          type="password"
          required
          autoFocus
          focused
          fullWidth
          variant="outlined"
          margin="dense"
          error={validationTriggered && pin.length > 0 && pin.length < 4}
          helperText={validationTriggered && pin.length > 0 && pin.length < 4 ? 'PIN muss 4 Ziffern haben' : ''}
          sx={{
            maxWidth: 'none !important',
            width: '100%',
            '&.MuiTextField-root': {
              maxWidth: 'none !important'
            }
          }}
        />

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {String(error)}
          </Alert>
        )}
      </Box>
    </BaseModal>
  );
};

export default PinConfirmationModal;
